import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { BusyIcon } from '../../components/loading';
import { ExportMenu, type ExportMenuHandle } from '../../components/AppToolbar';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { GridSearch } from '../../components/GridSearch';
import { useHotkeys, formatCombo } from '../../components/hotkeys';

/** One scheduled maintenance task (a live dbo.MaintenanceTaskCheckList row). */
interface TaskRow {
  oid: string;
  taskID?: string;
  category?: string;
  taskDescription?: string;
  ownership?: string;
  recurrence?: string;
  frequency?: string;
  freqCode?: string;
  reminderDays?: string;
  maintenanceId?: string;
  dateToMaintain?: string;
  maintenanceStatus?: number;
}

/** A TaskCheckList master offered in the Link picker. */
interface MasterRow {
  id: string;
  taskID?: string;
  category?: string;
  taskDescription?: string;
  department?: string;
  ownership?: string;
}

type FreqName = 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
const FREQS: FreqName[] = ['Daily', 'Weekly', 'Monthly', 'Yearly'];

// Link picker columns — the TaskCheckList master, as before.
const PICK_COLUMNS: AppColumn<MasterRow>[] = [
  { field: 'taskID', header: 'TaskID', width: 90 },
  { field: 'category', header: 'Category', width: 170 },
  { field: 'taskDescription', header: 'Task/Description' },
];

/** The legacy "Task Checklist" child grid on the Maintenance Setup detail — the
 *  scheduling engine (Stage 1: Link / Unlink / list / export). Link picks
 *  TaskCheckList masters + a simplified recurrence (Frequency · Interval · Start ·
 *  End-by/Count · #DaysInAdvance) and the API generates one task per occurrence.
 *  Unlink soft-deletes the not-yet-maintained rows of a series. Built from the
 *  same shared primitives as the sibling child grids (`.sd-child`, `.ca-btn`,
 *  `.qs-*`, ExportMenu, BusyIcon) so it matches them exactly. */
export function MaintenanceTaskChecklistGrid({ parentId, viewOnly }: {
  parentId: string | null;
  viewOnly: boolean;
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // Ref to the shared ExportMenu so the Alt+X shortcut can open its dropdown.
  const exportRef = useRef<ExportMenuHandle>(null);

  const load = useCallback(async () => {
    if (!parentId) { setRows([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/maintenance-setups/${parentId}/task-checklists`);
      setRows((res.data?.data ?? []) as TaskRow[]);
    } catch {
      notify.error('Failed to load task checklist.');
    } finally {
      setLoading(false);
    }
  }, [parentId, notify]);

  useEffect(() => { void load(); }, [load]);

  // ── Link picker + recurrence ────────────────────────────────────────────────
  const [linkOpen, setLinkOpen] = useState(false);
  const [masters, setMasters] = useState<MasterRow[]>([]);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [freq, setFreq] = useState<FreqName>('Weekly');
  const [interval, setIntervalN] = useState(1);
  const [start, setStart] = useState(today);
  const [endMode, setEndMode] = useState<'date' | 'count'>('count');
  const [endBy, setEndBy] = useState('');
  const [count, setCount] = useState(4);
  const [reminderDays, setReminderDays] = useState(0);

  const openLink = async () => {
    setSearch(''); setPick(new Set());
    setFreq('Weekly'); setIntervalN(1); setStart(today);
    setEndMode('count'); setEndBy(''); setCount(4); setReminderDays(0);
    try {
      const res = await api.get('/api/v1/maintenance-setups/task-checklist-options');
      setMasters((res.data?.data ?? []) as MasterRow[]);
      setLinkOpen(true);
    } catch {
      notify.error('Failed to load task checklists.');
    }
  };

  const doLink = async () => {
    if (pick.size === 0 || !parentId) return;
    setBusy(true);
    try {
      const body = {
        taskChecklistIds: [...pick],
        frequency: freq,
        interval: Math.max(1, interval),
        startDate: start,
        endByDate: endMode === 'date' ? (endBy || null) : null,
        occurrenceCount: endMode === 'count' ? Math.max(1, count) : null,
        reminderDays,
        subject: null,
      };
      const res = await api.post(`/api/v1/maintenance-setups/${parentId}/task-checklists/link`, body);
      const made = res.data?.data?.count ?? 0;
      setLinkOpen(false);
      notify.success(`Linked ${pick.size} checklist(s) — ${made} task(s) generated.`);
      void load();
    } catch {
      notify.error('Link failed.');
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!selected || !parentId) return;
    const ok = await confirm({
      title: 'Unlink', tone: 'danger', confirmLabel: 'Unlink',
      message: 'Unlink this task series? Its not-yet-maintained tasks are removed; any maintained/skipped ones are kept.',
    });
    if (!ok) return;
    try {
      const res = await api.post(`/api/v1/maintenance-setups/${parentId}/task-checklists/unlink`, { taskOid: selected });
      setSelected(null);
      notify.success(`Unlinked ${res.data?.data?.count ?? 0} task(s).`);
      void load();
    } catch {
      notify.error('Unlink failed.');
    }
  };

  // Export — the current task rows in the legacy column order.
  const COLS: { key: keyof TaskRow; label: string }[] = [
    { key: 'taskID', label: 'TaskID' },
    { key: 'category', label: 'Category' },
    { key: 'taskDescription', label: 'Task/Description' },
    { key: 'ownership', label: 'Ownership' },
    { key: 'recurrence', label: 'Recurrence' },
    { key: 'frequency', label: 'Frequency' },
    { key: 'freqCode', label: 'FreqCode' },
    { key: 'reminderDays', label: '#DaysInAdvance' },
  ];
  const exportRows = rows.map((r) => COLS.map((c) => (r[c.key] == null ? '' : String(r[c.key]))));

  const term = search.trim().toLowerCase();
  const candidates = term
    ? masters.filter((m) => `${m.taskID} ${m.category} ${m.taskDescription}`.toLowerCase().includes(term))
    : masters;
  const toggle = (id: string) => setPick((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  // Keyboard shortcuts for this child grid's toolbar (standing rule 14) — each
  // mirrors its button's enabled state.
  useHotkeys([
    { combo: 'alt+r', label: 'Refresh', group: 'Task Checklist', order: 1, disabled: loading, run: () => void load() },
    { combo: 'alt+l', label: 'Link', group: 'Task Checklist', order: 2, disabled: viewOnly || !parentId, run: () => void openLink() },
    { combo: 'alt+k', label: 'Unlink', group: 'Task Checklist', order: 3, disabled: viewOnly || !selected, run: () => void unlink() },
    { combo: 'alt+x', label: 'Export to', group: 'Task Checklist', order: 4, run: () => exportRef.current?.open() },
  ]);

  return (
    // sd-child-bordered frames the whole section (title · toolbar · grid · footer)
    // in a card, same as the EDD Builder Query Details grid (owner request 2026-10-02).
    <div className="sd-static sd-child sd-child-bordered">
      <h3 className="sd-sub-h">Task Checklist</h3>
      <div className="sd-child-bar">
        <button type="button" className="ca-btn" disabled={loading} onClick={() => void load()}
          title={`Refresh (${formatCombo('alt+r')})`}>Refresh</button>
        <button type="button" className="ca-btn" disabled={viewOnly || !parentId}
          title={`${!parentId ? 'Save the maintenance setup first' : 'Link'} (${formatCombo('alt+l')})`}
          onClick={() => void openLink()}>Link</button>
        <button type="button" className="ca-btn" disabled={viewOnly || !selected}
          title={`Unlink (${formatCombo('alt+k')})`}
          onClick={() => void unlink()}>Unlink</button>
        <ExportMenu ref={exportRef} title={`Export to (${formatCombo('alt+x')})`}
          filename="Task Checklist" headers={COLS.map((c) => c.label)} rows={exportRows} />
      </div>
      <div className="sd-static-scroll">
        <table className="sd-static-tbl">
          <thead>
            <tr>
              <th style={{ width: 34 }} aria-label="Select" />
              {COLS.map((c) => <th key={c.key}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.oid}>
                <td style={{ textAlign: 'center' }}>
                  <input type="radio" name="mtc-task" checked={selected === r.oid}
                    onChange={() => setSelected(r.oid)} aria-label="Select row" />
                </td>
                {COLS.map((c) => <td key={c.key}>{r[c.key] == null ? '' : String(r[c.key])}</td>)}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLS.length + 1} className="sd-child-empty">
                  {loading ? 'Loading…' : 'No data to display'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="sd-static-note sd-child-foot">
        <span>Total Records: {rows.length}</span>
      </div>

      {/* Link popup on the shared AppDialog + AppDataGrid (owner request 2026-10-16, "use standard design"):
          Search · a paged multi-select grid of the TaskCheckList masters · the recurrence in the standard
          side-label layout (square, plain white, 26px boxes) · OK / Cancel. */}
      {linkOpen && (
        <AppDialog
          open
          title="Link Task Checklist"
          width={760}
          className="dlg-center"
          maximizable
          onClose={() => { if (!busy) setLinkOpen(false); }}
          footer={
            <>
              <button type="button" className="ca-btn ca-btn-solid" onClick={() => void doLink()}
                disabled={busy || pick.size === 0 || !start || (endMode === 'date' && !endBy)}>
                <BusyIcon busy={busy} icon="ti-link" /> OK
              </button>
              <button type="button" className="ca-btn" onClick={() => setLinkOpen(false)} disabled={busy}>Cancel</button>
            </>
          }
        >
          <div className="mtc-top">
            <span className="mtc-count">{pick.size} selected</span>
            {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
            <div data-hotkeys-ignore>
              <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
            </div>
          </div>
          <AppDataGrid<MasterRow>
            rows={candidates}
            columns={PICK_COLUMNS}
            getRowId={(m) => m.id}
            defaultPageSize={10}
            selectable
            selectedIds={pick}
            onSelectionChange={(s) => setPick(new Set([...s].map(String)))}
            onRowClick={(m) => toggle(m.id)}
            emptyText="No task checklists to link."
          />

          <div className="mtc-rec" data-unsaved-ignore>
            <div className="mtc-rec-h">Recurrence</div>
            <div className="mtc-grid">
              <label className="mtc-l" htmlFor="mtc-freq">Frequency:</label>
              <select id="mtc-freq" className="mtc-in" value={freq} onChange={(e) => setFreq(e.target.value as FreqName)}>
                {FREQS.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <label className="mtc-l" htmlFor="mtc-days">#DaysInAdvance:</label>
              <input id="mtc-days" className="mtc-in" type="number" min={0} value={reminderDays}
                onChange={(e) => setReminderDays(Number(e.target.value) || 0)} />

              <label className="mtc-l" htmlFor="mtc-every">Every:</label>
              <input id="mtc-every" className="mtc-in" type="number" min={1} value={interval}
                onChange={(e) => setIntervalN(Number(e.target.value) || 1)} />
              <label className="mtc-l" htmlFor="mtc-start">Start:</label>
              <input id="mtc-start" className="mtc-in" type="date" value={start} onChange={(e) => setStart(e.target.value)} />

              <span className="mtc-l">End:</span>
              <div className="mtc-end">
                <label className="mtc-opt">
                  <input type="radio" name="mtc-end" checked={endMode === 'count'} onChange={() => setEndMode('count')} />
                  After
                </label>
                <input className="mtc-in mtc-in--n" type="number" min={1} value={count} disabled={endMode !== 'count'}
                  aria-label="Occurrences" onChange={(e) => setCount(Number(e.target.value) || 1)} />
                <span>occurrence(s)</span>
              </div>
              <span className="mtc-l" aria-hidden />
              <div className="mtc-end">
                <label className="mtc-opt">
                  <input type="radio" name="mtc-end" checked={endMode === 'date'} onChange={() => setEndMode('date')} />
                  By
                </label>
                <input className="mtc-in" type="date" value={endBy} disabled={endMode !== 'date'}
                  aria-label="End by date" onChange={(e) => setEndBy(e.target.value)} />
              </div>
            </div>
          </div>
        </AppDialog>
      )}
    </div>
  );
}
